
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, HostListener, Component, Output, EventEmitter, OnDestroy, inject } from "@angular/core";
import { TasksConstants } from "../../shared/task-constacts";
import { getSelectedFilterOption, TasksRootState, getTagList, getSelectedFilterType, getFolderList, getLocationList } from "../../store/index";
import { Store } from "@ngrx/store";
import { Broadcaster } from "../../../common/providers/broadcaster.service";
import { List, dialogType } from "../../models";
import { TaskRepository } from "../../repository/task.repository";
import { getOnlineStatus } from "../../../reducers";
import { CreateListDialogComponent } from "../create-list-dialog/create-list-dialog.component";
import { CommonUtil } from "../../../common/utils/common.utils";
import { FolderOptionListItemComponent } from "./folder-option-list-item.component";
import { TaskConfirmDialogComponent } from "../task-dialogs/confirm-dialog.component";
import { MessageTranslatorService } from "../../services";
import { ErrorService } from "../../../common/providers/error-service";
import { SuccessService } from "../../../common/providers/success-service";
import { ErrorType, SuccessType } from "../../shared/task-enum";
import { Router } from "@angular/router";
import { Location } from "../../models/location";
import { CreateLocationDialogComponent } from "../create-location-dialog/create-location-dialog.component";
import { takeWhile, take } from "rxjs/operators";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { VncLibraryService, VNCButtonComponent, VNCSpinnerComponent } from "@uxf/vnc-library";
import { MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { FormsModule } from "@angular/forms";
import { MatList, MatListItem } from "@angular/material/list";
import { VNCLongPressDirective } from "../../../shared/directives/long-press.directive";
import { TranslatePipe } from "@ngx-translate/core";
import { TaskProjectSearchPipe } from "../pipes/task-project-search.pipe";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";

@Component({
    selector: "vp-location-option-list",
    template: `
  <div class="task__dialog" id="locationDialog">
    <div class="task__dialog-header">
      <div class="mobile-back-button">
        <button mat-button (click)="hide()">
          <mat-icon class="disable-select">close</mat-icon>
        </button>
      </div>
      <div class="header_lbl disable-select">
        {{'LOCATIONS' | translate}}
      </div>
      <div class="desktop-close-button">
        <button mat-button (click)="hide()">
          <mat-icon class="disable-select">close</mat-icon>
        </button>
      </div>
    </div>
    <div class="task__dialog-body folder-option-list_dialog">
      <mat-form-field class="filter-search-textfield">
        <mat-label>{{ 'SEARCH_LOCATION' | translate }}</mat-label>
        <input matInput id="locationSearchField" [(ngModel)]="searchText">
      </mat-form-field>

      <mat-list role="list" id="locationItems">
        @for (location of locationList | vpTaskProjectSearch : searchText; track location; let i = $index) {
          <mat-list-item role="listitem" class="disable-select" vpLongPress [timeout]="500" (onLongPress)="$event.stopPropagation();openContextMenu(location)"  [class.selected]="i == selectedIndex"  (click)="$event.stopPropagation();navigateRoute(location.name)">
            <div class="tag-list-item-block">
              <svg width="18px" height="18px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                <title>Icon/product/location-new</title>
                <g id="Icon/product/location-new" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                  <g id="Icon-24px" transform="translate(4.000000, 1.000000)" fill="#000000">
                    <path d="M8,0 C12.4228571,0 16,3.443 16,7.7 C16,9.65461538 15.0835503,11.9242604 13.8710168,14.0717708 L13.5334511,14.6540989 C13.476122,14.7505394 13.4182977,14.8466454 13.3600364,14.942376 L13.0056914,15.5119246 C12.9459153,15.6059889 12.8858188,15.6995955 12.8254602,15.7927035 L12.4606281,16.3450387 L12.0923004,16.8834349 L11.7232736,17.4059213 L11.3563441,17.9105274 L10.9943086,18.3952825 L10.6399636,18.8582157 L10.2961056,19.2973566 L8.76209811,21.1029683 C8.40451496,21.5238635 7.7734336,21.575188 7.35253844,21.2176049 C7.31128664,21.1825583 7.2729485,21.1442201 7.23790189,21.1029683 L5.7038944,19.2973566 L5.184,18.6296 L4.82546017,18.1555095 L4.46062813,17.6605826 L4.09230041,17.14679 C4.03077651,17.0596952 3.96922349,16.9718965 3.90769959,16.8834349 L3.53937187,16.3450387 L3.17453983,15.7927035 L2.816,15.2284 C2.69784615,15.0385231 2.5812071,14.8469799 2.46654893,14.6540989 L2.12898316,14.0717708 C0.916449704,11.9242604 0,9.65461538 0,7.7 C0,3.443 3.57714286,0 8,0 Z M8,2 C4.66497074,2 2,4.56503434 2,7.7 C2,8.69322797 2.3458444,9.96053154 3.01409327,11.4203421 C3.64939915,12.8081876 4.54667863,14.3111997 5.63770505,15.8676171 C6.30819597,16.8241143 7.01937804,17.7553435 7.73858591,18.6369503 L8,18.952 L8.26141409,18.6369503 C8.80081999,17.9757452 9.33571138,17.2866275 9.85230757,16.5798717 L10.362295,15.8676171 C11.4533214,14.3111997 12.3506009,12.8081876 12.9859067,11.4203421 C13.6541556,9.96053154 14,8.69322797 14,7.7 C14,4.56503434 11.3350293,2 8,2 Z M8,5.5 C9.38,5.5 10.5,6.62 10.5,8 C10.5,9.38 9.38,10.5 8,10.5 C6.62,10.5 5.5,9.38 5.5,8 C5.5,6.62 6.62,5.5 8,5.5 Z" id="Combined-Shape"></path>
                  </g>
                </g>
              </svg>
              <span class="list-item disable-select">{{ location.name }}</span>
            </div>
            <div class="rounded-rectangle">
              <span class="disable-select">{{ location.tasks_count }}</span>
            </div>
          </mat-list-item>
        }
      </mat-list>
    </div>
    <vnc-button id="newLocationButton" (click)="openAddLocationDialog()" class="add_list-button add-task-mobile-button" [diameter]="'large'" [shape]="'circle'" [roundButtonType]="'blue'">
      <i class="material-icons disable-select">add_location</i>
    </vnc-button>
  </div>
  @if (isLoading) {
    <div class="vnctask-loading">
      <div class="indicator">
        @if (true) {
          <vnc-spinner [size]="'medium'"></vnc-spinner>
        }
      </div>
    </div>
  }
  `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatIcon, MatFormField, FormsModule, MatList, MatListItem, VNCLongPressDirective, VNCButtonComponent, VNCSpinnerComponent, TranslatePipe, TaskProjectSearchPipe, MatInput, MatLabel]
})

export class LocationOptionListComponent implements OnDestroy {
  private router = inject(Router);
  private store = inject<Store<TasksRootState>>(Store);
  private broadcaster = inject(Broadcaster);
  private changerDetectorRef = inject(ChangeDetectorRef);
  private taskRepo = inject(TaskRepository);
  private messageTranslatorService = inject(MessageTranslatorService);
  private vncLibaryService = inject(VncLibraryService);
  private successService = inject(SuccessService);
  private errorService = inject(ErrorService);
  private matDialog = inject(MatDialog);
  private dialogRef = inject<MatDialogRef<LocationOptionListComponent>>(MatDialogRef);

  selectedFilter: string;
  isAlive: boolean = true;
  taskConstants = TasksConstants;
  locationList: Location[] = [];
  isLongPressed: boolean =  false;
  isLoading: boolean = false;
  dialogType = dialogType;
  searchText: string = "";
  isOnline: boolean = false;
  selectedFilterType: string;
  currDelSelectedLocation: Location;

  constructor() {
    this.setupStore();
    this.handleDeleteLocationSuccessMessages();
    this.handleLocationErrorMessages();

    this.broadcaster.on<any>("closeAllMdlDialogs").pipe(takeWhile(() => this.isAlive))
      .subscribe(presence => {
        this.hide();
      });

    this.broadcaster.on<any>("hideLocationOptionListDialog").pipe(takeWhile(() => this.isAlive))
      .subscribe(presence => {
        this.hide();
      });

    this.store.select(getOnlineStatus).pipe(takeWhile(() => this.isAlive)).subscribe((isOnline) => {
      this.isOnline = isOnline;
      this.changerDetectorRef.markForCheck();
    });
  }

  setupStore() {
    this.store
      .select(getSelectedFilterOption)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(value => {
        this.selectedFilter = value;
        this.changerDetectorRef.markForCheck();
      });

    this.store
    .select(getLocationList)
    .pipe(takeWhile(() => this.isAlive))
    .subscribe(locations => {
      if (locations) {
        this.locationList = locations;
        this.changerDetectorRef.markForCheck();
      }
    });

    this.store.select(getOnlineStatus).pipe(takeWhile(() => this.isAlive)).subscribe((isOnline) => {
      this.isOnline = isOnline;
      this.changerDetectorRef.markForCheck();
    });

    this.store.select(getSelectedFilterType).pipe(takeWhile(() => this.isAlive)).subscribe(value => {
      this.selectedFilterType = value;
      this.changerDetectorRef.markForCheck();
    });
  }

  handleLocationErrorMessages() {
    this.errorService.only(ErrorType.LocationError).pipe(takeWhile(() => this.isAlive)).subscribe(error => {
      this.vncLibaryService.openSnackBar(error.messages, "","", "", 3000, "bottom", "left").subscribe(res => {
      });
      this.isLoading = false;
      this.changerDetectorRef.markForCheck();
    });
  }

  handleDeleteLocationSuccessMessages() {
    this.successService.only(SuccessType.LocationDeleted).pipe(takeWhile(() => this.isAlive)).subscribe(success => {
      this.vncLibaryService.openSnackBar(success.messages, "","", "", 3000, "bottom", "left").subscribe(res => {
      });
      this.isLoading = false;
      this.changerDetectorRef.markForCheck();

      this.taskRepo.getLocationsWithCounters();
      if (this.selectedFilterType === TasksConstants.ROUTE_TYPE_LOCATION) {
        if (this.isSelectedFilter(this.currDelSelectedLocation.name)) {
          this.router.navigate(["/task"]);
        }
      }
    });
  }

  navigateRoute(title: any) {
    if (CommonUtil.isOnIOS() && this.isLongPressed) {
      this.isLongPressed = false;
      return;
    }
    this.hide(title);
  }

  isSelectedFilter(title: string): boolean {
    if (title === this.selectedFilter) {
      return true;
    }
    return false;
  }

  @HostListener("document:keydown.esc", ["$event"])
  hide(title?) {
    this.dialogRef.close({ type: this.taskConstants.ROUTE_TYPE_LOCATION, title: title});
  }

  ngOnDestroy(): void {
    this.isAlive = false;
    this.changerDetectorRef.markForCheck();
  }

  openAddLocationDialog() {
    const dlg = this.matDialog.open(CreateLocationDialogComponent, {
      maxWidth: "100%",
      autoFocus: false,
      panelClass: "vp-create-location-dialog",
      data: { isCreate: true, selectedLocation: null }
    });
  }

  openContextMenu(location) {
    this.isLongPressed = true;
    const pDialog = this.matDialog.open(FolderOptionListItemComponent, {
      maxWidth: "100%",
      autoFocus: false,
      panelClass: "vp-folder-option-list-item-dialog",
    });
    pDialog.afterClosed().pipe(take(1)).subscribe( value => {
      if (value) {
        if (value.type === TasksConstants.RENAME) {
          this.renameLocation(location);
        } else if (value.type === TasksConstants.DELETE) {
          this.deleteLocation(location);
        }
      }
    });
  }

  renameLocation(location) {
    const dlg = this.matDialog.open(CreateLocationDialogComponent, {
      maxWidth: "100%",
      autoFocus: false,
      panelClass: "vp-create-location-dialog",
      data: { isCreate: false, selectedLocation: location }
    });
  }

  deleteLocation(location) {
    if (!this.isOnline) {
      this.broadcaster.broadcast("OFFLINE_CONNECTION", true);
      return;
    }

    const dlg = this.matDialog.open(TaskConfirmDialogComponent, {
      maxWidth: "100%",
      autoFocus: false,
      panelClass: "vp-task-confirm-dialog",
      data: { type: this.dialogType.DELETE, message: this.messageTranslatorService.getMessage(TasksConstants.CONFIRM_DELETE_ATTACHMENT) + " \"" + location.name + "\"?", header: null }
    });
    dlg.afterClosed().pipe(take(1)).subscribe(res => {
      if (!!res) {
        this.isLoading = true;
        this.currDelSelectedLocation = location;
        this.taskRepo.deleteLocation(location);
        this.changerDetectorRef.markForCheck();
      }
    });
  }
}
